import { createClient } from '@supabase/supabase-js'
import { AppState } from 'react-native'
import { env } from './env'
import { SecureStorageAdapter } from './storage/secure-storage'
// The 15s-timeout fetch wrapper used to be defined inline here, which is why it
// only ever protected calls made through this client. It now lives in
// lib/utils/fetch-with-timeout.ts so every remote call in the app can use the
// same one — a never-settling fetch is a never-releasing busy latch.
import { fetchWithTimeoutAdapter } from './utils/fetch-with-timeout'
import type { Database } from './types/database'

// env module throws on import if required vars are missing, so reaching this
// line guarantees both values are present and non-empty.
const supabaseUrl = env.EXPO_PUBLIC_SUPABASE_URL
const supabaseAnonKey = env.EXPO_PUBLIC_SUPABASE_ANON_KEY

if (__DEV__) {
  console.log('[Tago] Creating Supabase client, URL:', supabaseUrl ? 'set' : 'MISSING')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: SecureStorageAdapter,
    autoRefreshToken: true,
    persistSession: true,
    // The root layout owns the web PKCE callback explicitly so it can also
    // finish registration consent when OAuth was launched from register.tsx.
    // Leaving Supabase auto-detection on creates a race/double-exchange for
    // the same `?code=` callback. Native Google uses signInWithIdToken.
    detectSessionInUrl: false,
    flowType: 'pkce',
  },
  global: {
    fetch: fetchWithTimeoutAdapter,
  },
})

if (__DEV__) console.log('[Tago] Supabase client created')

// AppState-aware auto-refresh: start when app is active, stop when backgrounded
AppState.addEventListener('change', (state) => {
  if (state === 'active') {
    supabase.auth.startAutoRefresh()
  } else {
    supabase.auth.stopAutoRefresh()
  }
})
